import "./styles.scss";
import * as React from "react";
import { useContext, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Dropdown, DropdownItemProps } from "semantic-ui-react";
import { PublicReviewWidgetReview, ReviewServices } from "@/services/bedrock";
import { Review } from "@/services/bedrock/dtos/Review";
import { ReviewSummaryLine } from "@/components/ShareDesignPost/ReviewPickerRow";
import { useDebounced } from "@/utils/useDebounced";
import { PlayerCardSettingsContext } from "../Contexts";

interface ReviewSelectorProps {
  value: number | null;
  onChange: (value: number | null) => void;
  playerId: number | undefined;
  selectedReview?: PublicReviewWidgetReview | null;
}

const ReviewSelector: React.FC<ReviewSelectorProps> = ({ value, onChange, playerId, selectedReview }) => {
  const { User } = useContext(PlayerCardSettingsContext);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 300);

  const { data: reviews = [], isFetching } = useQuery({
    queryKey: ["playerCardReviewOptions", playerId, debouncedSearch],
    queryFn: () =>
      ReviewServices.getReviews({
        playerId,
        status: "published",
        active: true,
        search: debouncedSearch || undefined,
        perPage: 25,
      }).then(page => page.content.filter(review => review.approvedForSocial !== false)),
    enabled: !!playerId && playerId > 0,
    placeholderData: keepPreviousData,
  });

  const options: DropdownItemProps[] = useMemo(() => {
    const reviewOptions = reviews.map(review => ({
      key: review.id,
      value: Number(review.id),
      text: <ReviewSummaryLine review={review} />,
    }));
    if (value && selectedReview && !reviewOptions.some(option => option.value === value)) {
      reviewOptions.unshift({
        key: String(value),
        value,
        text: <ReviewSummaryLine review={selectedReview as Review} />,
      });
    }
    return reviewOptions;
  }, [reviews, value, selectedReview]);

  return (
    <Dropdown
      className="reviewSelector"
      fluid
      selection
      search={(items: DropdownItemProps[]) => items}
      placeholder="Search reviews"
      noResultsMessage={isFetching ? "Searching..." : "No published reviews found."}
      loading={isFetching}
      disabled={!User.hasWritePermission}
      options={options}
      value={value ?? undefined}
      searchQuery={search}
      onSearchChange={(_e, data) => setSearch(data.searchQuery)}
      onChange={(_e, data) => {
        setSearch("");
        onChange((data.value as number) ?? null);
      }}
    />
  );
};
export default ReviewSelector;
